import { Injectable } from "@angular/core";
import { PaymentMethodGetDto } from "clearline-api";
import { BehaviorSubject, Subject } from "rxjs";

export interface ChargebeePaymentServiceConfig {
  htmlContentContainerId: string;
  paymentMethodData?: PaymentMethodGetDto;
}

type ChargebeePaymentComponent = { validate: () => Promise<boolean>; confirm: () => Promise<unknown>; close: () => void };
type ChargebeeInstance = { components: (options: unknown) => ChargebeeComponents };
type ChargebeeComponents = { create: (type: string, options: unknown, callbacks: Record<string, (...args: any[]) => void>) => unknown };

@Injectable()
export class ChargebeePaymentService {
  private paymentComponent: ChargebeePaymentComponent | null = null;
  private isSubmitButtonValid$ = new BehaviorSubject<boolean>(false);

  canSubmit$ = this.isSubmitButtonValid$.asObservable();
  onSuccess$ = new Subject<string>();
  isProcessing$ = new BehaviorSubject<boolean>(false);

  initPaymentIntent(config: ChargebeePaymentServiceConfig, account: string, publishableKey: string): void {
    this.addChargeBeeScript(config, account, publishableKey);
  }

  submit(): void {
    this.isProcessing$.next(true);
    this.paymentComponent?.validate().then((isValid: boolean) => {
      if (isValid) {
        this.paymentComponent?.confirm().catch((error: any) => {
          console.error("Payment confirmation failed:", error);
          this.isProcessing$.next(false);
        });
      } else {
        console.warn("Payment component is not valid.");
        this.isProcessing$.next(false);
      }
    });
  }

  cleanup(): void {
    this.paymentComponent?.close();
  }

  private addChargeBeeScript(config: ChargebeePaymentServiceConfig, account: string, publishableKey: string): void {
    const cbId = "chargebee-payment-content";
    const script = document.getElementById(cbId);

    if (script) {
      this.initializePaymentComponent(config, account, publishableKey);
      return;
    }

    const node = document.createElement("script");
    node.src = "https://js.chargebee.com/v2/chargebee.js";
    node.type = "text/javascript";
    node.id = cbId;
    node.setAttribute("cbSite", "true");
    node.onload = () => {
      this.initializePaymentComponent(config, account, publishableKey);
    };

    document.getElementsByTagName("head")[0].appendChild(node);
  }

  private initializePaymentComponent(
    { htmlContentContainerId, paymentMethodData }: ChargebeePaymentServiceConfig,
    account: string,
    publishableKey: string
  ): void {
    try {
      const chargebee = (window as any).Chargebee;

      chargebee.init({
        site: account,
        publishableKey
      });

      const chargebeeInstance = chargebee?.getInstance();
      const paymentComponent = this.createPaymentComponent(chargebeeInstance, paymentMethodData?.paymentIntent);

      if (!this.paymentComponent) {
        paymentComponent.mount(`#${htmlContentContainerId}`);
      }

      this.paymentComponent = paymentComponent;
    } catch (error) {
      console.error("Failed to initialize Chargebee component:", error);
      return;
    }
  }

  private createPaymentComponent(chargebeeInstance: ChargebeeInstance, paymentIntent: unknown): any {
    const componentOptions = {
      locale: "en"
    };

    const paymentComponentOptions = {
      paymentIntent,
      layout: {
        type: "tab",
        showRadioButtons: false
      },
      paymentMethods: {
        sortOrder: ["card"],
        allowed: ["card"]
      }
    };

    const components: ChargebeeComponents = chargebeeInstance.components(componentOptions);

    const onSuccess = async (payment_intent: { id: string }, extra: unknown) => {
      this.onSuccess$.next(payment_intent.id);
      console.log("Payment successful:", payment_intent, extra);
    };

    const onError = (error: unknown) => {
      // Handle payment and payment button errors here.
      console.log(error);
      this.isProcessing$.next(false);
    };

    const onPaymentMethodChange = (paymentMethod: unknown) => {
      // Triggered on first render of the payment component and when user selects a different payment method.
      console.log(paymentMethod);
    };

    const onButtonClick = async () => {
      // Triggered whenever the user attempts to submit the payment.
      // Validate user input or run any critical checks here.
      // Ensure that this function returns within one second.
      // If your checks pass, return a resolved Promise to initiate payment submission.

      return Promise.resolve();
      // If your checks fail, return a rejected Promise with a `reason` to block payment submission.
      // For example:
      // return Promise.reject(reason);
      // `onError()` is called automatically with `reason` as the argument.
    };

    const onClose = () => {
      console.log("component closed");
    };

    return components.create("payment", paymentComponentOptions, {
      onError,
      onSuccess,
      onPaymentMethodChange,
      onButtonClick,
      onClose
    });
  }
}
